import React, { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LoadingOverlay from "react-loading-overlay-ts";
import useAuth from "../hooks/useAuth";
import { useNavigate, useParams } from "react-router-dom";
import { GoogleSheetsService } from '../services/GoogleSheetsService';
import { Show } from '../services/GoogleSheetsService';
import ReplyIcon from "@mui/icons-material/Reply";


interface ShowResultRow {
  rowIndexId: number;
  division: string;
  riderName: string;
  horseName: string;
  time: string;
  showId: string;
}

const ShowResultPage: React.FC = () => {
  const user = useAuth();
  const navigate = useNavigate();
  const { showId } = useParams<{ showId?: string }>();
  const [shows, setShows] = useState<Show[]>([]);
  const [selectedShowId, setSelectedShowId] = useState<string | null>(showId || null);
  const [showResults, setShowResults] = useState<ShowResultRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all shows on mount
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const getService = async () => {
      let service;
       if (user.user) {
         service = await GoogleSheetsService.fromFirebaseUser(user.user);
      } else {
        service = new GoogleSheetsService();
      }
      return service;
    };
    getService()
      .then(async (service) => {
        if (!service) throw new Error('Could not create GoogleSheetsService');
        const allShows = await service.getAllShows();
        if (mounted) setShows(allShows);
      })
      .catch((err) => setError('Failed to load shows'))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [user]);

  // Fetch show results when a show is selected
  useEffect(() => {
    if (!selectedShowId) return;
    let mounted = true;
    setLoading(true);
    const getService = async () => {
      let service;
       if (user.user) {
         service = await GoogleSheetsService.fromFirebaseUser(user.user);
      } else {
        service = new GoogleSheetsService();
      }
      return service;
    };
    getService()
      .then(async (service) => {
        if (!service) throw new Error('Could not create GoogleSheetsService');
        const results = await service.getShowResultsByShowId(selectedShowId);
        if (mounted) setShowResults(results);
      })
      .catch((err) => setError('Failed to load show results'))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [selectedShowId, user]);

  const handleShowClick = (showId: string) => {
    setSelectedShowId(showId);
  };

  const goBack = () => {
    navigate('/showResults');
  };

  return (
    <LoadingOverlay active={loading} className="loading-overlay">
      <Container maxWidth="sm" sx={{ marginBottom: "60px" }} className="page-header">
        {selectedShowId ? (
          <>
            <ReplyIcon onClick={goBack} style={{ cursor: 'pointer' }} />
            <h2>Show Results</h2>
            {showResults.length === 0 && <div>No results for this show.</div>}
            {/* ...existing code... */}
              {/* Group results by division and render with division headers */}
              {Object.entries(
                showResults.reduce((acc, result) => {
                  if (!acc[result.division]) acc[result.division] = [];
                  acc[result.division].push(result);
                  return acc;
                }, {} as Record<string, ShowResultRow[]>)
              ).map(([division, results]) => (
                <Box key={division} sx={{ mb: 3 }}>
                  <Box
                    className="show-result-division-name"
                    sx={{
                      background: '#21969b',
                      color: 'white',
                      borderRadius: '20px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '24px',
                      width: '100%',
                      mb: 1.5,
                      mt: 1.5,
                      fontSize: '1.1rem',
                      letterSpacing: 1,
                      boxSizing: 'border-box',
                    }}
                  >
                    {division.toUpperCase()}
                  </Box>
                  <Stack spacing={0.5}>
                    {results.map((result) => (
                      <Grid className="show-result-row" style={{ display: 'flex', gap: 8 }} key={result.rowIndexId}>
                        <div className="show-result-name" style={{ flex: 4 }}><Box>{result.riderName}</Box></div>
                        <div className="show-result-horse" style={{ flex: 4 }}><Box>{result.horseName}</Box></div>
                        <div className="show-result-time" style={{ flex: 2 }}><Box>{result.time}</Box></div>
                      </Grid>
                    ))}
                  </Stack>
                </Box>
              ))}
          </>
        ) : (
          <>
            <h2>Show List</h2>
            {shows.length === 0 && <div>No shows available.</div>}
            <Stack spacing={2}>
              {shows.map((show) => (
                <Button
                  key={show.showId}
                  variant="outlined"
                  onClick={() => handleShowClick(show.showId)}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  fullWidth
                >
                  <strong>{show.showName}</strong> <span style={{ marginLeft: 8, color: '#888' }}>{show.showDateTime}</span>
                </Button>
              ))}
            </Stack>
          </>
        )}
        {error && <div style={{ color: 'red' }}>{error}</div>}
      </Container>
    </LoadingOverlay>
  );
};

export default ShowResultPage;
